import { Box, VStack, Grid, Separator } from "@chakra-ui/react";
import SectionHeader from "../../../CustomUi/SectionHeader";
import { BiPlus } from "react-icons/bi";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import { Level } from "../../../../services/Dtos";
import { useState } from "react";
import Loader from "../../../CustomUi/Loader";
import CustomAlert from "../../../CustomUi/CustomAlert";
import LevelModal from "./LevelModal";
import LevelCard from "./LevelCard";
import { getNextOrder } from "../../../../services/Utils";
import Empty from "../../../CustomUi/Empty";
import useIcons from "../../../../hooks/useIcons";
import Cad from "../../../CustomUi/Cad";
import { useDisclosure } from "@chakra-ui/react";

function LevelSettings() {
  const { levels, promotionRules, isLoading, error, accentColor } =
    useSchoolsContext();
  const [nextOrder, setNextOrder] = useState(0);
  const [levelToEdit, setLevelToEdit] = useState<Level | null>(null);
  const icons = useIcons();
  const [activeTab, setActiveTab] = useState(0);

  const { open: isOpen, onOpen, onClose } = useDisclosure();

  const handleModalClose = () => {
    setLevelToEdit(null);
    onClose();
  };

  const handleRefresh = () => {
    // Refresh functionality can be implemented here
  };

  const handleAddLevel = () => {
    setLevelToEdit(null);
    setNextOrder(getNextOrder(levels));
    setActiveTab(0); // Reset to first tab
    onOpen();
  };

  const handleEditLevel = (level: Level) => {
    setLevelToEdit(level);
    onOpen();
  };

  const handlePromotionRuleClick = (level: Level) => {
    setLevelToEdit(level);
    setActiveTab(1);
    onOpen();
  };

  const hasPromotionRule = (levelId: string) => {
    // Safety check for invalid levelId
    if (!levelId) {
      return false;
    }
    // Check if the level has promotion rules by looking for a promotion rule with matching levelId
    const hasRule = promotionRules.some((rule) => rule.levelId === levelId);
    return hasRule;
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <CustomAlert message={error} handleClick={handleRefresh} />;
  }

  return (
    <VStack gap={8}>
      <LevelModal
        isOpen={isOpen}
        onClose={handleModalClose}
        levelToEdit={levelToEdit}
        nextOrder={nextOrder}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {/* Levels Management Section */}
      <Cad>
        <VStack gap={6} align="stretch">
          <SectionHeader
            title="School Levels"
            subtitle="Create and manage your school's academic levels and promotion rules"
            icon={icons.main.level}
            buttonText="Add Level"
            onButtonClick={handleAddLevel}
            buttonIcon={BiPlus}
            buttonColorScheme={accentColor}
          />

          <Separator />

          {/* Levels Grid */}
          {levels.length > 0 ? (
            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                xl: "repeat(3, 1fr)",
              }}
              gap={6}
            >
              {levels
                .filter((s) => s && s.id) // Filter out undefined or invalid levels
                .map((s) => (
                  <LevelCard
                    key={s.id}
                    level={s}
                    handleClick={() => handleEditLevel(s)}
                    onPromotionRuleClick={handlePromotionRuleClick}
                    hasPromotionRule={hasPromotionRule(s.id)}
                    loadingPromotionRules={false}
                  />
                ))}
            </Grid>
          ) : (
            <Box py={8}>
              <Empty
                message="No levels created yet"
                hasButton
                buttonText="Create First Level"
                handleClick={handleAddLevel}
              />
            </Box>
          )}
        </VStack>
      </Cad>
    </VStack>
  );
}

export default LevelSettings;
